import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import GradeService from '../../services/grade.service';

const GradeForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      fetchGrade();
    }
  }, [id, isEdit]);

  const fetchGrade = async () => {
    setLoading(true);
    try {
      const response = await GradeService.get(id);
      const grade = response.data?.data;
      setFormData({
        name: grade.name || '',
      });
    } catch (error) {
      console.error('Error loading grade:', error);
      showSnackbar(t('grades.errorLoadingGrade'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showSnackbar(t('grades.pleaseFillRequired'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await GradeService.update(id, formData);
        showSnackbar(t('grades.gradeUpdatedSuccess'));
      } else {
        await GradeService.create(formData);
        showSnackbar(t('grades.gradeCreatedSuccess'));
      }
      navigate('/grades');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            `Error ${isEdit ? 'updating' : 'creating'} grade`);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/grades')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('grades.editGrade') : t('grades.newGrade')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('grades.updateGrade') : t('grades.saveGrade')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('grades.gradeName')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default GradeForm;
